import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useApiTokensMutations from "../../datahooks/useApiTokensMutations";

import type { ApiToken } from "../../types";

interface DeleteTokenDialogProps {
  apiToken: ApiToken;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export default function DeleteTokenDialog({
  apiToken,
  isOpen,
  onOpenChange,
}: DeleteTokenDialogProps) {
  const isDesktop = useTwBreakpoint("lg");

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  const { name, id } = apiToken || {};
  const { deleteApiToken, isDeletingToken } = useApiTokensMutations();

  const handleDelete = () => {
    deleteApiToken(id).then(() => {
      onOpenChange(false);
    });
  };

  return (
    <Component open={isOpen} onOpenChange={onOpenChange}>
      <Content>
        <Title>Delete API Key?</Title>

        <Description className="mb-12 lg:mb-8">
          Are you sure you want to delete the API key named{" "}
          <span className="font-semibold text-black-950">
            {name?.length > 20 ? `${name?.slice(0, 30)}...` : name}
          </span>
          ? This action cannot be undone. Deleting this key will immediately
          revoke access for any applications or services using it
        </Description>

        <div className="flex justify-between gap-x-2.5">
          <Close asChild>
            <Button
              className="lg:flex-1"
              variant="secondary-black"
              disabled={isDeletingToken}
            >
              Cancel
            </Button>
          </Close>

          <Button
            className="lg:flex-1"
            variant="outline-danger"
            onClick={handleDelete}
            isLoading={isDeletingToken}
          >
            Delete
          </Button>
        </div>
      </Content>
    </Component>
  );
}
